import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isEmpty } from 'lodash';
import styled from '@emotion/styled';
import {
    BodyTextMedium,
    Button,
    HeadingText50 as StepTitle,
    pxToRem,
} from '@modernhealth/vital-components';

import { mediaQueries } from 'src/shared/styles/variables';
import { navigationService } from 'src/config/rootService';
import tada from 'src/public/img/tada.svg';
import {
    CareNavigationRequestContactPreference,
    CareNavigationRequestSource,
} from 'src/generated/webapp_gql';

import { CareNavigationRequest } from '../models/careNavigation';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationAllSetStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const Image = styled.img({
    width: '75%',
});

const ButtonGroupContainer = styled.div({
    marginBottom: pxToRem(24),
    width: '100%',
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
    maxWidth: '70%',
});

export const CareNavigationAllSetStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();

    const descriptionCopy =
        location.state.contactPreference === CareNavigationRequestContactPreference.Phone
            ? t('careNavigation:care_navigation_all_set_step.phone_description')
            : t('careNavigation:care_navigation_all_set_step.email_description');

    let url = '/dashboard';
    let buttonCopy = t('careNavigation:care_navigation_all_set_step.back_home');
    switch (location.state.source) {
        case CareNavigationRequestSource.BenefitsPage:
            url = '/all-your-benefits';
            buttonCopy = t('careNavigation:care_navigation_all_set_step.back_benefits');
            break;
        case CareNavigationRequestSource.WellbeingReport:
            url = '/wellbeing-report';
            buttonCopy = t('careNavigation:care_navigation_all_set_step.back_wellbeing_report');
            break;
        case CareNavigationRequestSource.CareOptions:
            url = '/care-options';
            buttonCopy = t('careNavigation:care_navigation_all_set_step.back_care_options');
            break;
        default:
            break;
    }

    const handleBack = () => {
        navigationService.navigate(url);
    };

    return (
        <Wrapper>
            <Image src={tada} alt={t('careNavigation:care_navigation_all_set_step.title')} />

            <TitleContainer>
                <StepTitle centered>
                    {t('careNavigation:care_navigation_all_set_step.title')}
                </StepTitle>
                <BodyTextMedium centered>{descriptionCopy}</BodyTextMedium>
            </TitleContainer>

            <ButtonGroupContainer>
                <Button
                    variant="secondary"
                    data-testid={CareNavigationAllSetStepTestId.FinishButton}
                    fluid
                    size="large"
                    onClick={handleBack}
                >
                    {buttonCopy}
                </Button>
            </ButtonGroupContainer>
        </Wrapper>
    );
};
